import { useState, useEffect } from 'react';
import axios from 'axios';
import { CalendarOff, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
}

export default function ShopClosureControl({ closureUntil, onChange }) {
  const [days, setDays] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setDays('');
  }, [closureUntil]);

  const isClosed = closureUntil && new Date(closureUntil) > new Date();

  const handleSet = async () => {
    const n = parseInt(days, 10);
    if (!n || n < 1 || n > 90) {
      toast.error('Inserisci un numero di giorni tra 1 e 90');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.put(`${API}/shops/my/closure`, { days: n }, { withCredentials: true });
      onChange(res.data.closure_until);
      toast.success(`Chiuso per ${n} giorni`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/shops/my/closure`, { days: 0 }, { withCredentials: true });
      onChange(null);
      toast.success('Chiusura rimossa');
    } catch {
      toast.error('Errore');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3" data-testid="closure-control">
      <div className="flex items-center gap-2">
        <CalendarOff className="w-5 h-5 text-amber-600" />
        <h3 className="font-medium text-slate-800">Chiusura straordinaria</h3>
      </div>

      {isClosed ? (
        <>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-sm text-amber-800">
              Negozio chiuso fino al <strong>{formatDate(closureUntil)}</strong>.
              I clienti vedranno l'avviso "Chiuso fino al {formatDate(closureUntil)}".
            </p>
          </div>
          <Button
            type="button"
            onClick={handleClear}
            variant="outline"
            disabled={loading}
            className="w-full rounded-xl"
            data-testid="closure-clear"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Riapri il negozio
          </Button>
        </>
      ) : (
        <>
          <p className="text-xs text-slate-500">
            Chiudi temporaneamente il negozio per ferie, malattia o altre necessità.
            I clienti vedranno l'avviso e il negozio non risulterà disponibile.
          </p>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-xs text-slate-600">Numero di giorni</label>
              <Input
                type="number"
                min={1}
                max={90}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                placeholder="es. 7"
                className="rounded-xl mt-1"
                data-testid="closure-days-input"
              />
            </div>
            <Button
              type="button"
              onClick={handleSet}
              disabled={loading || !days}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl"
              data-testid="closure-set"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Chiudi negozio
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
